// src/api/auth.ts
// Modified fetchProfile to return null on 401 Unauthorized (based on existing auth.ts) :contentReference[oaicite:0]{index=0}&#8203;:contentReference[oaicite:1]{index=1}

const RAW_API_URL = process.env.REACT_APP_API_URL;
const API_BASE = RAW_API_URL?.replace(/\/$/, '') || '/api';

export interface AuthResponse {
  error?: string;
  message?: string;
  token?: string;
}

export interface Profile {
  username: string;
}

/**
 * Log in with username + password.
 * On success the server will set an HTTP-only cookie.
 */
export async function login(username: string, password: string): Promise<AuthResponse> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username, password }),
  });
  const data: AuthResponse = await res.json();
  if (!res.ok) {
    throw new Error(data.error || 'Login failed');
  }
  return data;
}

/**
 * Clear the server cookie.
 */
export async function logout(): Promise<AuthResponse> {
  const res = await fetch(`${API_BASE}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });
  const data: AuthResponse = await res.json();
  if (!res.ok) {
    throw new Error(data.error || 'Logout failed');
  }
  return data;
}

/**
 * Fetch the current user's profile.
 * Returns `null` if unauthorized (swallows 401).
 */
export async function fetchProfile(): Promise<Profile | null> {
  const res = await fetch(`${API_BASE}/profile`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });

  if (res.status === 401) {
    // Not logged in yet—silently return null
    return null;
  }

  if (!res.ok) {
    const errPayload = await res.json();
    throw new Error(errPayload.error || 'Failed to fetch profile');
  }

  return res.json();
}
